import React, { useEffect, useMemo } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { getRoleGroupForRoles, useInviteRoleGroups } from './InviteRoleGroups'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { useGetRolesQuery } from '../../store/role/role'
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getRoleDisplayName } from '../../components/Roles/utils'
import { Role } from '../../models/Role'
import { useGetGrantByIdQuery, useUpdateGrantByIdMutation } from '../../store/crossAccount/crossAccount'

export interface EditGrantModalProps {
  grantId: string
  show: boolean
  onHide: () => void
}

type FormValues = {
  roleIds: string[]
}

export const EditGrantModal: React.FC<EditGrantModalProps> = ({ grantId, show, onHide }) => {
  const accountId = useAppSelector(selectAccountId)

  const defaultValues = {
    roleIds: [],
  }
  const [updateGrant, { isLoading, isSuccess }] = useUpdateGrantByIdMutation()

  const handleSave = (data: FormValues) => {
    updateGrant({
      accountId,
      grantId,
      roleIds: data.roleIds,
    })
  }

  useEffect(() => {
    if (isSuccess) {
      onHide()
    }
  }, [isSuccess, onHide])

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit } = form

  const body = (
    <Container>
      <Row>
        <FormProvider {...form}>
          <EditGrantModalForm grantId={grantId} show={show} onHide={onHide} />
        </FormProvider>
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<h4>Edit Grant</h4>}
      headerBodySeparator
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionLoading={isLoading}
      centered={true}
    />
  )
}

type RoleDisplay = Role & { disabled: boolean }

const EditGrantModalForm: React.FC<EditGrantModalProps> = ({ grantId }) => {
  const { register, control, setValue } = useFormContext<FormValues>()

  const accountId = useAppSelector(selectAccountId)
  const roleGroups = useInviteRoleGroups({ onlyCrossAccountGrantable: true })

  const { data: grant, isFetching: isFetchingUserData } = useGetGrantByIdQuery({ accountId, grantId }, { refetchOnMountOrArgChange: true })

  const { data: roleData, isFetching: isFetchingRoleData } = useGetRolesQuery(
    {
      accountId: accountId,
      pageRequest: { page: 0, size: 500 },
    },
    { refetchOnMountOrArgChange: true },
  )

  const areUserRolesInGroup = grant ? getRoleGroupForRoles(grant.roleIds) !== null : false

  useEffect(() => {
    if (grant) {
      setValue('roleIds', grant.roleIds)
    }
  }, [grant, setValue])

  const roles = useMemo<RoleDisplay[]>(() => {
    if (!roleData || !grant) {
      return []
    }
    const groupRoleIds = new Set(roleGroups.flatMap((rg) => rg.roleIds))
    const userRoleIds = new Set(grant.roleIds)
    return roleData.data
      .filter((role) => {
        return groupRoleIds.has(role.id) || userRoleIds.has(role.id)
      })
      .map((role) => {
        const roleGroup = getRoleGroupForRoles([role.id])
        return {
          ...role,
          name: roleGroup?.name || role.name,
          disabled: !groupRoleIds.has(role.id) && userRoleIds.has(role.id),
        }
      })
  }, [roleData, grant, roleGroups])

  const handleRoleGroupSelected = (option: SelectOption) => {
    const roleGroup = roleGroups.find((e) => e.name === option.id)
    setValue('roleIds', roleGroup?.roleIds ?? [])
  }

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {isFetchingUserData || isFetchingRoleData ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <Controller
                name='roleIds'
                control={control}
                render={({ field }) => (
                  <>
                    <Form.Label>Roles</Form.Label>
                    {areUserRolesInGroup ? (
                      <SelectDropdown
                        className='zocks-px-0.75 py-2'
                        options={roleGroups.map((roleGroup) => {
                          return { id: roleGroup.name, label: roleGroup.name }
                        })}
                        initialSelectedOption={
                          roleGroups
                            .map((roleGroup) => {
                              return { id: roleGroup.name, label: roleGroup.name }
                            })
                            .find((option) => option.id === getRoleGroupForRoles(field.value)?.name) ?? null
                        }
                        onOptionSelected={handleRoleGroupSelected}
                      />
                    ) : (
                      <MultiSelectDropdown
                        register={register}
                        name='roleIds'
                        options={
                          roles.map((role) => {
                            return { id: role.id, label: getRoleDisplayName(role), disabled: role.disabled }
                          }) || []
                        }
                        initialSelectedOptions={field.value}
                      />
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}
